import { type VariantProps, cva } from "class-variance-authority";
import React from "react";

import { cn } from "@/utils/core/generalUtils";

export const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2 py-1 text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 font-normal",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        primaryLight:
          "border-transparent bg-primaryLight text-primary hover:bg-primaryLight/80",
        success:
          "border-transparent bg-success text-success-foreground hover:bg-success/80",
        successLight:
          "border-transparent bg-success/20 text-success-foreground text-success font-normal",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        secondaryWithBorder:
          "border-[0.5px] border-[#81838F80] bg-[#2844BD1A] text-[#5E5873] hover:opacity-90  transition-all",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        light:
          " border-transparent bg-primaryLight text-primaryLight-foreground hover:opacity-90  transition-all",
        yellow:
          " border-transparent bg-[#FDA602] text-white hover:opacity-90  transition-all",
        purple:
          "border-transparent bg-[#3758F914] text-[#111928]  hover:opacity-90  transition-all",
        lightSuccess:
          "border-transparent bg-lightSucess text-[#158246] hover:opacity-90  transition-all",
        lightYellow:
          "border-transparent bg-lightYellow text-[#946417] hover:opacity-90  transition-all",
        draft:
          "border-transparent bg-gray-50 text-gray-700 hover:opacity-90  transition-all",
        violet:
          "border-transparent bg-[#7367F1]/15 text-[#7367F1]  hover:opacity-90  transition-all",
        lightDestructive:
          "border-transparent bg-[#FEEBEB] text-[#FF5B5B] hover:opacity-90  transition-all",
        brightSkyBlue:
          "border-transparent bg-[#00C2FF1F] text-[#00C2FF] hover:opacity-90  transition-all",
        lightViolet:
          "border-transparent bg-[#7367F014] text-[#7367F0] hover:opacity-90  transition-all",
        lightVioletWithBorder:
          "border-[0.5px] border-[#7367F080] bg-[#7367F014] text-[#7367F0] hover:opacity-90  transition-all",
        lightSuccessWithBorder:
          "border-[0.5px] border-[#28C76F80] bg-[#61C4781F] text-[#28C76F] hover:opacity-90  transition-all",
        lightDestructiveWithBorder:
          "border-[0.5px]  border-[#DA081980] bg-[#DA081926] text-[#DA0819] hover:opacity-90  transition-all",
        purpleWithBorder:
          "border-[0.5px] border-[#7367F180] bg-[#7367F114] text-[#7367F1] hover:opacity-90  transition-all",
        orangeWithBorder:
          "border-[0.5px] border-[#FF8C0080] bg-[#FF8C0014] text-[#FF8C00] hover:opacity-90  transition-all",
        skyBlueWithBorder:
          "border-[0.5px] border-[#00C2FF80] bg-[#00C2FF14] text-[#00C2FF] hover:opacity-90  transition-all",
        blueWithBorder:
          "border-[0.5px] border-[#143E9F80] bg-[#143E9F1F] text-[#143E9F] hover:opacity-90  transition-all",
        draftWithBorder:
          "border-[0.5px] border-[#E9EEF4] bg-[#F3F4F6] text-[#1F2937] hover:opacity-90  transition-all",
        yellowWithBorder:
          "border-[0.5px] border-[#F4BA5A80] bg-[#F4BA5A1F] text-[#FFB743] hover:opacity-90  transition-all",
        greyWithBorder:
          "border-[0.5px] border-[#81838F80] bg-[#81838F14] text-[#808390] hover:opacity-90  transition-all",
        pinkWithBorder:
          "border-[0.5px] border-[#E532F780] bg-[#E532F71F] text-[#EB00FF] hover:opacity-90  transition-all",
        warning:
          "border-[0.5px] border-[#E532F780] bg-[#FB923C] text-[#EB00FF] hover:opacity-90  transition-all",
        lightWarning:
          "border-[0.5px] border-[#E5E5E5] bg-[#FB923C0D] text-[#FB923C] hover:opacity-90  transition-all",
        gray: "border border-[#E9EEF4] bg-[#F3F4F6] text-[#1F2937] hover:opacity-90  transition-all",
        muted:
          "border border-gray-300 bg-transparent text-gray-500 hover:opacity-90 transition-all",
        verified:
          "border border-green-500 bg-transparent text-green-500 hover:opacity-90 transition-all",
        unverified:
          "border border-red-500 bg-transparent text-red-500 hover:opacity-90 transition-all",
        statusTint: "border-transparent hover:opacity-90 transition-all",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export type BadgeVariants = VariantProps<typeof badgeVariants>["variant"];
export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

export const Badge = ({
  className,
  variant,
  ...props
}: BadgeProps): React.JSX.Element => {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
};
